import { request } from "@/api/request";

// D-CD-60 / REQ-CD-P1-CON-10 — the Console never navigates its own document
// to authenticate (SEC-CD-P1-27). Sign-in happens in a separate browsing
// context the operator opens deliberately, at the origin's own protected
// /api/* resource — Access's standard flow signs in and returns to that same
// URL on that same host. No login-endpoint URL of any kind is built here
// (D-CD-60 evidence item 3: every form tried answered 404 on this platform).

export type AccessOrigin = "defender" | "watcher";

/** The one table. Probe paths are cheap, read-only, operator-authorised GETs under /api/* (Access-fronted). */
export const ACCESS_TARGETS: Readonly<
  Record<AccessOrigin, { label: "Defender" | "Watcher"; baseUrl: string; probePath: "/api/mode" | "/api/status" }>
> = {
  defender: { label: "Defender", baseUrl: import.meta.env.VITE_DEFENDER_BASE_URL, probePath: "/api/mode" },
  watcher: { label: "Watcher", baseUrl: import.meta.env.VITE_WATCHER_BASE_URL, probePath: "/api/status" },
};

/** `${baseUrl without trailing slashes}${probePath}` — absolute, same host as the API, no query string. */
export function signInUrl(origin: AccessOrigin): string {
  const target = ACCESS_TARGETS[origin];
  return `${target.baseUrl.replace(/\/+$/, "")}${target.probePath}`;
}

/** Opens signInUrl(origin) in a new browsing context. Called ONLY synchronously from a click handler. */
export function openSignIn(origin: AccessOrigin): void {
  window.open(signInUrl(origin), "_blank", "noopener,noreferrer");
}

/** GET probePath through request(); resolves on 2xx, rejects with request()'s ApiError otherwise. */
export async function probeAccess(origin: AccessOrigin): Promise<void> {
  const target = ACCESS_TARGETS[origin];
  await request(target.baseUrl, target.probePath);
}
